<template>
  <v-container v-bind:class="{ 'pa-0': $vuetify.breakpoint.xs }">
    <v-row no-gutters justify="center">
      <v-col cols="12" sm="10" md="10" lg="8" xl="6">
        <v-card outlined>
          <v-card-title
            v-text="$t('account.creator.title')"
            class="font-weight-bold primary--text"
          ></v-card-title>
          <v-divider></v-divider>
          <v-stepper v-model="stepperModel" alt-labels>
            <v-stepper-header>
              <template v-for="(step, index) in registrationSteps">
                <v-stepper-step
                  :step="index + 1"
                  :key="`${index}-step`"
                  color="secondary"
                  :complete="stepperModel > index + 1"
                  class="text-center"
                >
                  {{ step.name }}
                </v-stepper-step>
                <v-divider
                  :key="`${index}-divider`"
                  v-if="index != registrationSteps.length - 1"
                ></v-divider>
              </template>
            </v-stepper-header>
            <v-divider></v-divider>
            <v-progress-linear
              indeterminate
              color="secondary"
              v-show="loadingStep"
            ></v-progress-linear>
            <v-stepper-items>
              <v-stepper-content step="1">
                <div class="pb-6 d-flex justify-center align-center">
                  <div class="d-flex flex-wrap justify-center">
                    <div class="primary--text text-h5 font-weight-medium">
                      {{ $t("account.creator.steps.createCreator") }}
                    </div>
                    <div
                      class="hidden-md-and-up pt-2 text-center"
                      style="width:80%"
                      v-text="$t('account.creator.aboutGemsChest')"
                    ></div>
                  </div>
                  <v-tooltip top v-model="showGemsChestTooltip">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        v-on="on"
                        @click="showGemsChestTooltip = true"
                        class="hidden-sm-and-down ml-4"
                      >
                        <v-icon color="primary">help_outline</v-icon>
                      </v-btn>
                    </template>
                    <span v-text="$t('account.creator.aboutGemsChest')"> </span>
                  </v-tooltip>
                </div>
                <!-- Your Gems Chest -->
                <template>
                  <v-form
                    ref="gemsChestForm"
                    v-model="isGemsChestFormValid"
                    lazy-validation
                  >
                    <v-row no-gutters justify="center">
                      <v-slide-y-transition group>
                        <v-col
                          v-for="(website,
                          websiteIndex) in creatorTemplateToEdit.websites"
                          :key="`website-${websiteIndex}`"
                        >
                          <v-sheet
                            :color="
                              websiteIndex % 2 === 0 ? '#dea70f10' : '#104f5510'
                            "
                            class="pa-4"
                          >
                            <div
                              class="d-flex align-center justify-space-between pb-4"
                            >
                              <span
                                class="text-h6"
                                v-text="`Site internet n° ${websiteIndex + 1}`"
                              ></span>
                              <!-- RemoveWebsiteButton -->
                              <v-btn
                                @click="
                                  creatorTemplateToEdit.removeWebsite(
                                    websiteIndex
                                  )
                                "
                                icon
                                small
                              >
                                <v-icon>close</v-icon>
                              </v-btn>
                            </div>
                            <v-text-field
                              outlined
                              name="websiteUrl"
                              v-bind:label="$t('signUp.websiteUrl')"
                              id="websiteUrl"
                              color="primary"
                              v-model="
                                creatorTemplateToEdit.websites[websiteIndex].url
                              "
                              :rules="websiteUrlRules"
                              required
                              background-color="white"
                              type="url"
                              :ref="`websiteUrlInput${websiteIndex}`"
                              :error-messages="$t(errors.websiteUrlInput)"
                              @focus="
                                removeInputErrorMessage('websiteUrlInput')
                              "
                              @input="
                                removeInputErrorMessage('websiteUrlInput')
                              "
                            >
                              <template v-slot:append>
                                <v-icon color="primary">fas fa-link</v-icon>
                              </template>
                            </v-text-field>
                            <!-- WebsiteNameInput -->
                            <v-text-field
                              outlined
                              name="websiteName"
                              v-bind:label="$t('signUp.websiteName')"
                              id="websiteName"
                              background-color="white"
                              color="primary"
                              required
                              v-model="
                                creatorTemplateToEdit.websites[websiteIndex]
                                  .name
                              "
                              :rules="websiteNameRules"
                              :ref="`websiteNameInput${websiteIndex}`"
                              :error-messages="$t(errors.websiteNameInput)"
                            >
                              <template v-slot:append>
                                <v-icon color="primary">fas fa-font</v-icon>
                              </template>
                            </v-text-field>
                          </v-sheet>

                          <v-divider class="my-7"></v-divider>
                        </v-col>
                      </v-slide-y-transition>

                      <!-- AddWebsiteInput -->
                      <v-col
                        cols="12"
                        class="d-flex align-center justify-center mb-7"
                      >
                        <v-btn
                          @click="creatorTemplateToEdit.addWebsite()"
                          v-if="creatorTemplateToEdit.websites.length < 5"
                          v-text="$t('signUp.addWebsite')"
                          color="primary"
                          outlined
                          v-bind:class="{
                            'text-lowercase': $vuetify.breakpoint.xs
                          }"
                        >
                        </v-btn>
                      </v-col>

                      <!-- YouTube channels inputs -->
                      <v-slide-y-transition group>
                        <v-col
                          v-for="(youtubeChannel,
                          channelIndex) in creatorTemplateToEdit.youtubeChannels"
                          :key="`channel${channelIndex}`"
                        >
                          <!-- YoutubeChannel url -->
                          <v-sheet
                            :color="
                              creatorTemplateToEdit.websites.length % 2 == 0
                                ? channelIndex % 2 === 0
                                  ? '#dea70f10'
                                  : '#104f5510'
                                : channelIndex % 2 === 0
                                ? '#104f5510'
                                : '#dea70f10'
                            "
                            class="pa-4"
                          >
                            <div
                              class="d-flex align-center justify-space-between pb-4"
                            >
                              <span
                                class="text-h6"
                                v-text="`Chaîne YouTube n° ${channelIndex + 1}`"
                              ></span>

                              <!-- RemoveYoutubeChannelButton -->

                              <v-btn
                                @click="
                                  creatorTemplateToEdit.removeYoutubeChannel(
                                    channelIndex
                                  )
                                "
                                icon
                                small
                              >
                                <v-icon>close</v-icon>
                              </v-btn>
                            </div>
                            <v-text-field
                              outlined
                              name="YoutubeChannelUrl"
                              :label="$t('signUp.youtubeChannelUrl')"
                              color="primary"
                              background-color="white"
                              v-model="
                                creatorTemplateToEdit.youtubeChannels[
                                  channelIndex
                                ].url
                              "
                              :rules="youtubeChannelUrlRules"
                              required
                              :error-messages="
                                $t(errors.youtubeChannelUrlInput)
                              "
                              @focus="
                                removeInputErrorMessage(
                                  'youtubeChannelUrlInput'
                                )
                              "
                              @input="
                                removeInputErrorMessage(
                                  'youtubeChannelUrlInput'
                                )
                              "
                            >
                              <template v-slot:append>
                                <v-icon color="primary">fab fa-youtube</v-icon>
                              </template>
                            </v-text-field>
                          </v-sheet>
                          <v-divider class="my-7"></v-divider>
                          <!-- AddYoutubeChannelURLInput -->
                        </v-col>
                      </v-slide-y-transition>
                      <v-col
                        cols="12"
                        class="d-flex align-center justify-center"
                      >
                        <v-btn
                          @click="creatorTemplateToEdit.addYoutubeChannel()"
                          v-if="
                            creatorTemplateToEdit.youtubeChannels.length < 2
                          "
                          v-text="$t('signUp.addYoutubeChannel')"
                          color="primary"
                          outlined
                          v-bind:class="{
                            'text-lowercase': $vuetify.breakpoint.xs
                          }"
                        >
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-form>
                  <v-card-actions class="justify-end mt-7">
                    <v-btn text color="secondary" @click="stepperModel++">
                      Passer
                    </v-btn>
                    <v-btn
                      depressed
                      color="secondary"
                      :disabled="
                        (this.creatorTemplateToEdit.websites.length === 0 &&
                          this.creatorTemplateToEdit.youtubeChannels.length ===
                            0) ||
                          !isGemsChestFormValid
                      "
                      @click="submitGemsChest()"
                    >
                      Enregistrer
                    </v-btn>
                  </v-card-actions>
                </template>
              </v-stepper-content>
              <!-- Picture Profile -->
              <v-stepper-content step="2">
                <div class="pb-6 text-center">
                  <span
                    class="primary--text text-h5 font-weight-medium "
                    v-text="$t('account.creator.steps.profilePicture')"
                  ></span>
                  <br />
                  <span
                    v-text="
                      $t('account.creator.steps.profilePictureExplanation')
                    "
                  ></span>
                </div>
                <v-row no-gutters justify="center">
                  <v-col xl="8" lg="8" md="8" sm="10" cols="12">
                    <v-file-input
                      :disabled="disableStepInputs"
                      v-model="picture"
                      outlined
                      color="primary"
                      accept="image/png, image/jpeg"
                      :rules="
                        picture === null || picture === undefined
                          ? []
                          : pictureRules
                      "
                      :prepend-icon="''"
                      append-icon="fas fa-image"
                      ref="pictureInput"
                      :error-messages="errorMessages"
                      @focus="errorMessages = ''"
                      @input="errorMessages = ''"
                    >
                    </v-file-input>
                  </v-col>
                </v-row>
                <v-card-actions class="justify-end mt-7">
                  <div>
                    <v-btn text color="secondary" @click="stepperModel++">
                      Passer
                    </v-btn>
                    <v-btn @click.stop="uploadFile()" color="secondary">
                      Enregistrer
                    </v-btn>
                  </div>
                </v-card-actions>
              </v-stepper-content>
              <!-- About You  -->
              <v-stepper-content step="3">
                <div class="pb-6 text-center">
                  <span
                    class="primary--text text-h5 font-weight-medium "
                    v-text="$t('account.creator.steps.aboutYou')"
                  ></span>
                  <br />
                  <span
                    v-text="$t('account.creator.steps.aboutYouExplanation')"
                  ></span>
                </div>
                <v-form
                  ref="aboutYouForm"
                  v-model="isAboutYouFormValid"
                  lazy-validation
                >
                  <v-row no-gutters justify="center">
                    <v-col xl="8" lg="8" md="8" sm="10" cols="12">
                      <!-- firstNameInput -->
                      <v-text-field
                        :rules="aboutYouFirstName"
                        v-model="creatorTemplateToEdit.user.firstName"
                        color="primary"
                        outlined
                        :required="false"
                        v-bind:label="$t('profile.form.first_name')"
                        :hint="$t('profile.form.first_name')"
                      >
                        <template v-slot:append>
                          <v-icon color="primary">fas fa-font</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col xl="8" lg="8" md="8" sm="10" cols="12">
                      <!-- DescriptionInput -->
                      <v-textarea
                        outlined
                        color="primary"
                        counter="300"
                        required="false"
                        v-bind:label="$t('profile.form.description')"
                        v-model="creatorTemplateToEdit.description"
                        :rules="aboutYouDescription"
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-form>
                <v-card-actions class="justify-end mt-7">
                  <v-btn text color="secondary" @click="stepperModel++">
                    Passer
                  </v-btn>
                  <v-btn
                    @click="submitAboutYou"
                    color="secondary"
                    :disabled="!isAboutYouFormValid"
                  >
                    Enregistrer
                  </v-btn>
                </v-card-actions>
              </v-stepper-content>
              <!-- Social Network -->
              <v-stepper-content step="4">
                <div class="pb-6 text-center">
                  <span
                    class="primary--text text-h5 font-weight-medium "
                    v-text="$t('account.creator.steps.socialNetworks')"
                  ></span>
                  <br />
                  <span
                    v-text="
                      $t('account.creator.steps.socialNetworksExplanation')
                    "
                  ></span>
                </div>
                <v-form
                  ref="socialNetworksForm"
                  v-model="isSocialNetworksFormValid"
                  lazy-validation
                >
                  <v-row justify="center">
                    <v-col xl="8" lg="8" md="8" sm="10" cols="12">
                      <!-- InstagramURL Input -->
                      <v-text-field
                        outlined
                        v-model="creatorTemplateToEdit.instagramUrl"
                        :rules="instagramUrlRules"
                        :required="false"
                        color="primary"
                        v-bind:label="$t('profile.form.instagramAccount')"
                        append-icon="fab fa-instagram"
                      >
                      </v-text-field>
                    </v-col>
                    <v-col xl="8" lg="8" md="8" sm="10" cols="12">
                      <!-- twitterURLInput -->

                      <v-text-field
                        v-model="creatorTemplateToEdit.twitterUrl"
                        :rules="twitterUrlRules"
                        outlined
                        color="primary"
                        v-bind:label="$t('profile.form.twitterAccount')"
                        append-icon="fab fa-twitter"
                      >
                      </v-text-field>
                    </v-col>
                    <v-col xl="8" lg="8" md="8" sm="10" cols="12">
                      <!-- facebookURLInput -->
                      <v-text-field
                        v-model="creatorTemplateToEdit.facebookUrl"
                        :rules="facebookUrlRules"
                        outlined
                        color="primary"
                        v-bind:label="$t('profile.form.facebookAccount')"
                        append-icon="fab fa-facebook"
                      >
                      </v-text-field>
                    </v-col>
                    <v-col xl="8" lg="8" md="8" sm="10" cols="12">
                      <!-- Pinterest Input -->
                      <v-text-field
                        v-model="creatorTemplateToEdit.pinterestUrl"
                        :rules="pinterestUrlRules"
                        outlined
                        color="primary"
                        v-bind:label="$t('profile.form.pinterestAccount')"
                        append-icon="fab fa-pinterest"
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-card-actions class="justify-end mt-7">
                    <div>
                      <v-btn text color="secondary" @click="stepperModel++">
                        Passer
                      </v-btn>
                      <v-btn
                        @click="submitAboutYou"
                        color="secondary"
                        :disabled="!isSocialNetworksFormValid"
                      >
                        Enregistrer
                      </v-btn>
                    </div>
                  </v-card-actions>
                </v-form>
              </v-stepper-content>
              <v-stepper-content step="5">
                <div
                  class="pb-4 primary--text text-h5 font-weight-medium text-center"
                >
                  {{ $t("account.creator.steps.done") }}
                </div>
                <v-row
                  align-content="center"
                  justify="center"
                  no-gutters
                  class="pa-4"
                >
                  <v-col cols="6" class="text-center pb-6">
                    <v-icon large color="success" v-text="'done'"></v-icon>
                  </v-col>
                  <v-col
                    class="text-h6 text-center pb-6"
                    cols="12"
                    v-text="$t('account.creator.startSharingGems')"
                  >
                  </v-col>
                  <router-link
                    :to="{
                      name: 'profile',
                      params: { username: creator.user.username }
                    }"
                  >
                    <v-btn
                      color="secondary"
                      depressed
                      @click="$store.dispatch('share/openShareModal')"
                      v-text="$t('app.shareAGem')"
                    ></v-btn>
                  </router-link>
                </v-row>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
/* Services */
import AccountService from "../../account/services/AccountService";
import ErrorInputMixins from "@/services/ErrorInputMixins";
import FormFunctions from "./../../services/FormFunctions";
/* Modules */
import { mapState } from "vuex";
import cloneDeep from "lodash/cloneDeep";

export default {
  mixins: [ErrorInputMixins],
  data() {
    return {
      creatorTemplateToEdit: {},
      loadingStep: false,
      disableStepInputs: false,
      registrationSteps: [
        {
          name: this.$t("account.creator.steps.createCreator")
        },
        {
          name: this.$t("account.creator.steps.profilePicture")
        },
        {
          name: this.$t("account.creator.steps.aboutYou")
        },
        {
          name: this.$t("account.creator.steps.socialNetworks")
        }
      ],
      stepperModel: 1,
      /* GEMS Chest */
      isGemsChestFormValid: true,
      showGemsChestTooltip: false,
      websiteNameRules: [
        v => !!v || this.$i18n.t("signUp.rules.websiteName.required"),
        v => v.length <= 100 || this.$i18n.t("signUp.rules.websiteName.max")
      ],
      websiteUrlRules: [
        v => !!v || this.$i18n.t("signUp.rules.websiteUrl.required"),
        v =>
          /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$|^$/gm.test(
            v
          ) || this.$i18n.t("signUp.rules.websiteUrl.valid")
      ],
      youtubeChannelUrlRules: [
        v => !!v || this.$i18n.t("signUp.rules.websiteUrl.required"),
        v =>
          /(^http(s)?:\/\/*)|^$/.test(v) ||
          this.$i18n.t("signUp.rules.youtubeChannelUrl.mustStartWithProcotole"),
        v =>
          /(^http(s)?:\/\/www.youtube.com\/channel\/)|^$/.test(v) ||
          this.$i18n.t("signUp.rules.youtubeChannelUrl.mustBeYoutubeChannelUrl")
      ],
      /* PICTURE PROFILE */
      picture: null,
      pictureRules: [
        value =>
          /^image\//.test(value.type) ||
          this.$i18n.t("profile.rules.picture.type"),
        value =>
          !value ||
          value.size < 4000000 ||
          this.$i18n.t("profile.rules.picture.size")
      ],
      errorMessages: "",
      /* ABOUT YOU */
      isAboutYouFormValid: true,
      aboutYouDescription: [
        v => v.length <= 300 || this.$i18n.t("profile.rules.description.max")
      ],
      aboutYouFirstName: [
        v => v.length <= 30 || this.$i18n.t("profile.rules.first_name.max"),
        v =>
          v.length === 0 ||
          /^[\wáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ\s]+$/gi.test(
            v
          ) ||
          this.$i18n.t("profile.rules.first_name.specialCaracters")
      ],
      /* SOCIAL ACCOUNT  */
      isSocialNetworksFormValid: true,
      instagramUrlRules: [
        v =>
          /(^(((http(s?):\/\/)|(http(s?):\/\/www.))|www.)instagram.com\/([\w]|\.)+(\/?)$)|^$/.test(
            v
          ) || this.$i18n.t("profile.rules.instagram_url")
      ],
      facebookUrlRules: [
        v =>
          /(^(((http(s?):\/\/)|(http(s?):\/\/www.))|www.)facebook.com\/[\w]+)|^$/.test(
            v
          ) || this.$i18n.t("profile.rules.facebook_url")
      ],
      twitterUrlRules: [
        v =>
          /(^(((http(s?):\/\/)|(http(s?):\/\/www.))|www.)twitter.com\/[\w]+)|^$/.test(
            v
          ) || this.$i18n.t("profile.rules.twitter_url")
      ],
      pinterestUrlRules: [
        v =>
          /(^(((http(s?):\/\/)|(http(s?):\/\/www.))|www.)pinterest.(com|fr|de)\/[\w]+(\/?)$)|^$/.test(
            v
          ) || this.$i18n.t("profile.rules.pinterest_url")
      ]
    };
  },
  methods: {
    setLoadingState(state) {
      this.disableStepInputs = state;
      this.loadingStep = state;
    },
    /* GEMS CHEST SECTION */
    submitGemsChest() {
      if (this.$refs.gemsChestForm.validate()) {
        this.setLoadingState(true);
        for (
          let index = 0;
          index < this.creatorTemplateToEdit.websites.length;
          index++
        ) {
          this.creatorTemplateToEdit.websites[
            index
          ].url = FormFunctions.urlBuilder(
            this.creatorTemplateToEdit.websites[index].url
          );
        }
        AccountService.editCreator(
          this.creatorTemplateToEdit.getCreatorDataForEditAPI()
        )
          .then(response => {
            this.stepperModel++;
            this.creatorTemplateToEdit = AccountService.createCreator(
              response.data
            );
            this.$store.commit("mutateCreator", response.data);
          })
          .finally(() => this.setLoadingState(false));
      }
    },
    /* PICTURE PROFILE SECTION */
    uploadFile() {
      if (this.picture != null) {
        this.setLoadingState(true);
        var picture = new FormData();
        picture.append("profile_picture", this.picture);
        this.$store
          .dispatch("editProfilePicture", picture)
          .then(creator => {
            this.stepperModel++;
            this.creatorTemplateToEdit = AccountService.createCreator(creator);
          })
          .finally(() => this.setLoadingState(false));
      } else {
        this.errorMessages = this.$i18n.t("profile.rules.picture.required");
      }
    },
    /* ABOUT YOU SECTION */
    submitAboutYou() {
      if (this.$refs.aboutYouForm.validate()) {
        this.setLoadingState(true);
        AccountService.editCreator(
          this.creatorTemplateToEdit.getCreatorDataForEditAPI()
        )
          .then(response => {
            this.creatorTemplateToEdit = AccountService.createCreator(
              response.data
            );
            this.stepperModel++;
            this.$store.commit("mutateCreator", response.data);
          })
          .finally(() => this.setLoadingState(false));
      }
    },
    /* SOCIAL NETWORKS SECTION */
    submitSocialNetworks() {
      if (this.$refs.isSocialNetworksFormValid.validate()) {
        this.setLoadingState(true);
        this.creatorTemplateToEdit.instagramUrl = FormFunctions.urlBuilder(
          this.creatorTemplateToEdit.instagramUrl
        );
        this.creatorTemplateToEdit.pinterestUrl = FormFunctions.urlBuilder(
          this.creatorTemplateToEdit.pinterestUrl
        );
        this.creatorTemplateToEdit.twitterUrl = FormFunctions.urlBuilder(
          this.creatorTemplateToEdit.twitterUrl
        );
        this.creatorTemplateToEdit.facebookUrl = FormFunctions.urlBuilder(
          this.creatorTemplateToEdit.facebookUrl
        );
        AccountService.editCreator(
          this.creatorTemplateToEdit.getCreatorDataForEditAPI()
        )
          .then(response => {
            this.stepperModel++;
            this.$store.commit("mutateCreator", response.data);
          })
          .finally(() => this.setLoadingState(false));
      }
    }
  },
  computed: {
    ...mapState({
      creator: state => state.myProfileView.creator
    }),
    websiteCreatorUrl() {
      return this.creatorTemplateToEdit.websites
        ? this.creatorTemplateToEdit.websites.map(x => x.url)
        : [];
    },
    youtubeChannelUrls() {
      return this.creatorTemplateToEdit.youtubeChannels
        ? this.creatorTemplateToEdit.youtubeChannels.map(x => x.url)
        : [];
    },
    indexOfWebsitesNameThatAreNotFill() {
      return this.creatorTemplateToEdit.websites
        ? this.creatorTemplateToEdit.websites
            .map(website =>
              website.url.length > 0 && website.name.length === 0 ? 1 : 0
            )
            .reduce((total, current) => total + current, 0)
        : 0;
    }
  },
  created() {
    this.creatorTemplateToEdit = AccountService.createCreator(
      cloneDeep(this.creator)
    );
  },
  watch: {
    websiteCreatorUrl(v) {
      const duplicate = [...new Set(v)];
      if (duplicate[0] != "" && duplicate.length != v.length) {
        this.duplicateWebisteUrl();
      }
    },
    youtubeChannelUrls(v) {
      const duplicate = [...new Set(v)];
      if (duplicate[0] != "" && duplicate.length != v.length) {
        this.duplicateYoutubeChannelUrl();
      }
    },
    indexOfWebsitesNameThatAreNotFill(value) {
      value
        ? this.messageAlertForNoWebsiteName()
        : this.removeMessageAlertForNoWebsiteName();
    }
  }
};
</script>

<style>
.v-stepper__label {
  text-align: center !important;
}
</style>
